import { globalizeSelectors } from '../Utils';
import type {
  FormulaSupportCase,
  FormulaSupportCasePage,
  FormulaSupportListParams,
} from '../../api/FormulaSupport';

export const actionTypes = {
  FETCH: 'FORMULA_SUPPORT/FETCH',
  FETCH_SKIPPED: 'FORMULA_SUPPORT/FETCH_SKIPPED',
  SET: 'FORMULA_SUPPORT/SET',
  ADD: 'FORMULA_SUPPORT/ADD',
  MARK_SEEN: 'FORMULA_SUPPORT/MARK_SEEN',
  UPDATED: 'FORMULA_SUPPORT/UPDATED',
  ARCHIVE: 'FORMULA_SUPPORT/ARCHIVE',
  ARCHIVED: 'FORMULA_SUPPORT/ARCHIVED',
  ERROR: 'FORMULA_SUPPORT/ERROR',
  UNAVAILABLE: 'FORMULA_SUPPORT/UNAVAILABLE',
  USE: 'FORMULA_SUPPORT/USE',
  USED: 'FORMULA_SUPPORT/USED',
  CREATE_FEEDBACK: 'FORMULA_SUPPORT/CREATE_FEEDBACK',
  CREATE_REQUEST: 'FORMULA_SUPPORT/CREATE_REQUEST',
  CREATE_SUCCEEDED: 'FORMULA_SUPPORT/CREATE_SUCCEEDED',
  LOOKUP_ORDER: 'FORMULA_SUPPORT/LOOKUP_ORDER',
  LOOKUP_SUCCEEDED: 'FORMULA_SUPPORT/LOOKUP_SUCCEEDED',
  LOOKUP_FAILED: 'FORMULA_SUPPORT/LOOKUP_FAILED',
  FEEDBACK_EXISTS: 'FORMULA_SUPPORT/FEEDBACK_EXISTS',
  CHANGED: 'FORMULA_SUPPORT/CHANGED',
  SOCKET_CONNECTED: 'FORMULA_SUPPORT/SOCKET_CONNECTED',
  FETCH_NOTIFICATIONS: 'FORMULA_SUPPORT/FETCH_NOTIFICATIONS',
  NOTIFICATIONS_SKIPPED: 'FORMULA_SUPPORT/NOTIFICATIONS_SKIPPED',
  SET_NOTIFICATIONS: 'FORMULA_SUPPORT/SET_NOTIFICATIONS',
  NOTIFICATIONS_ERROR: 'FORMULA_SUPPORT/NOTIFICATIONS_ERROR',
} as const;

export type FormulaSupportState = {
  cases: FormulaSupportCase[];
  lookupOrderId: number | null;
  lookupLoading: boolean;
  lookupError: boolean;
  orderFeedbackCase: FormulaSupportCase | null;
  loading: boolean;
  error: unknown;
  pending: boolean;
  creating: boolean;
  seeing: number[];
  total: number;
  page: number;
  pageSize: number;
  hasNext: boolean;
  query: FormulaSupportListParams;
  loaded: boolean;
  notificationCases: FormulaSupportCase[];
  notificationsLoading: boolean;
  notificationsError: unknown;
};

const initialState: FormulaSupportState = {
  lookupOrderId: null,
  lookupLoading: false,
  lookupError: false,
  orderFeedbackCase: null,
  cases: [],
  loading: false,
  error: null,
  pending: false,
  creating: false,
  seeing: [],
  total: 0,
  page: 1,
  pageSize: 25,
  hasNext: false,
  query: { page: 1, page_size: 25, archived: false, sort: 'desc' },
  loaded: false,
  notificationCases: [],
  notificationsLoading: false,
  notificationsError: null,
};

const actions = {
  fetch: (params = {}) => ({ type: actionTypes.FETCH, payload: params }),
  changed: (payload: { siteid: number; revision: string }) => ({
    type: actionTypes.CHANGED,
    payload,
  }),
  socketConnected: () => ({ type: actionTypes.SOCKET_CONNECTED }),
  fetchNotifications: () => ({ type: actionTypes.FETCH_NOTIFICATIONS }),
  setNotifications: (cases: FormulaSupportCase[]) => ({
    type: actionTypes.SET_NOTIFICATIONS,
    payload: cases,
  }),
  set: (result: FormulaSupportCasePage) => ({
    type: actionTypes.SET,
    payload: result,
  }),
  add: (item: FormulaSupportCase) => ({ type: actionTypes.ADD, payload: item }),
  markSeen: (caseid: number) => ({
    type: actionTypes.MARK_SEEN,
    payload: caseid,
  }),
  updated: (cases: FormulaSupportCase[]) => ({
    type: actionTypes.UPDATED,
    payload: cases,
  }),
  archive: (caseids: number[]) => ({
    type: actionTypes.ARCHIVE,
    payload: caseids,
  }),
  use: (caseid: number) => ({ type: actionTypes.USE, payload: caseid }),
  createFeedback: (payload: unknown) => ({
    type: actionTypes.CREATE_FEEDBACK,
    payload,
  }),
  lookupOrder: (orderid: number) => ({
    type: actionTypes.LOOKUP_ORDER,
    payload: orderid,
  }),
  createRequest: (payload: unknown) => ({
    type: actionTypes.CREATE_REQUEST,
    payload,
  }),
};

export default actions;

const localSelectors = {
  cases: (state: FormulaSupportState) => state.cases,
  loading: (state: FormulaSupportState) => state.loading,
  state: (state: FormulaSupportState) => state,
  unseen: (state: FormulaSupportState) =>
    state.notificationCases.filter(
      (item) =>
        item.status !== 'ARCHIVED' &&
        item.messages.some(
          (message) => message.sender === 'LAB' && !message.seen_at
        )
    ),
};

export const selectors = globalizeSelectors(localSelectors, 'formula_support');

export function reducer(
  state: FormulaSupportState = initialState,
  action: { type: string; payload?: unknown }
): FormulaSupportState {
  switch (action.type) {
    case actionTypes.LOOKUP_ORDER:
      return {
        ...state,
        lookupOrderId: action.payload as number,
        lookupLoading: true,
        lookupError: false,
        orderFeedbackCase: null,
      };
    case actionTypes.LOOKUP_SUCCEEDED:
      return {
        ...state,
        lookupLoading: false,
        orderFeedbackCase: action.payload as FormulaSupportCase | null,
      };
    case actionTypes.LOOKUP_FAILED:
      return { ...state, lookupLoading: false, lookupError: true };
    case actionTypes.FEEDBACK_EXISTS:
      if (
        (action.payload as { orderid: number }).orderid !== state.lookupOrderId
      ) {
        return { ...state, creating: false };
      }
      return {
        ...state,
        creating: false,
        lookupLoading: false,
        lookupError: false,
        orderFeedbackCase: (action.payload as { case: FormulaSupportCase })
          .case,
      };
    case actionTypes.CREATE_FEEDBACK:
    case actionTypes.CREATE_REQUEST:
      return { ...state, creating: true, error: null };
    case actionTypes.CREATE_SUCCEEDED:
      return { ...state, creating: false };
    case actionTypes.FETCH: {
      const query = {
        ...state.query,
        ...(action.payload as FormulaSupportListParams),
      };
      const changed =
        query.search !== state.query.search ||
        query.archived !== state.query.archived ||
        query.sort !== state.query.sort;
      return {
        ...state,
        loading: true,
        error: null,
        cases: changed ? [] : state.cases,
        hasNext: changed ? false : state.hasNext,
        query,
      };
    }
    case actionTypes.FETCH_SKIPPED:
      return { ...state, loading: false };
    case actionTypes.FETCH_NOTIFICATIONS:
      return { ...state, notificationsLoading: true, notificationsError: null };
    case actionTypes.NOTIFICATIONS_SKIPPED:
      return { ...state, notificationsLoading: false };
    case actionTypes.SET_NOTIFICATIONS:
      return {
        ...state,
        notificationCases: action.payload as FormulaSupportCase[],
        notificationsLoading: false,
        notificationsError: null,
      };
    case actionTypes.NOTIFICATIONS_ERROR:
      return {
        ...state,
        notificationsLoading: false,
        notificationsError: action.payload,
      };
    case actionTypes.SET: {
      const result = action.payload as FormulaSupportCasePage;
      const previousIds = new Set(
        state.cases.map((item) => item.supportcaseid)
      );
      return {
        ...state,
        cases:
          result.page === 1
            ? result.cases
            : [
                ...state.cases,
                ...result.cases.filter(
                  (item) => !previousIds.has(item.supportcaseid)
                ),
              ],
        total: result.total,
        page: result.page,
        pageSize: result.page_size,
        hasNext: result.has_next,
        loading: false,
        loaded: true,
      };
    }
    case actionTypes.ADD:
      return {
        ...state,
        cases: [action.payload as FormulaSupportCase, ...state.cases],
      };
    case actionTypes.MARK_SEEN:
      return {
        ...state,
        seeing: [...state.seeing, action.payload as number],
        error: null,
      };
    case actionTypes.ARCHIVE:
    case actionTypes.USE:
      return { ...state, pending: true, error: null };
    case actionTypes.USED:
      return { ...state, pending: false };
    case actionTypes.ARCHIVED:
    case actionTypes.UPDATED: {
      const updates = action.payload as FormulaSupportCase[];
      return {
        ...state,
        pending: action.type === actionTypes.ARCHIVED ? false : state.pending,
        seeing: state.seeing.filter(
          (id) => !updates.some((item) => item.supportcaseid === id)
        ),
        cases: state.cases.map(
          (item) =>
            updates.find(
              (update) => update.supportcaseid === item.supportcaseid
            ) || item
        ),
      };
    }
    case actionTypes.ERROR:
      return {
        ...state,
        loading: false,
        pending: false,
        creating: false,
        seeing: [],
        error: action.payload,
      };
    case actionTypes.UNAVAILABLE:
      return {
        ...state,
        loading: false,
        pending: false,
        creating: false,
        lookupLoading: false,
        seeing: [],
      };
    default:
      return state;
  }
}
